'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { RiMapPin2Line } from 'react-icons/ri';
import { format } from 'date-fns';

import type { JobPortalOffer } from '@eliadmin/core/src/api/jobportal/offers-api';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { formatNumber } from './jobportal-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { CategoryBadge } from './card/category-badge';
import { getFileStreamUrl } from '@eliadmin/core/src/api/video/video-api';
import { useRef, useState } from 'react';
import { PlayCircle, Star } from 'lucide-react';
import { FiBriefcase } from 'react-icons/fi';

export interface JobOfferCardSmallProps {
  offer: JobPortalOffer;
}

export const JobOfferCardSmall = ({ offer }: JobOfferCardSmallProps) => {
  const { t } = useTranslations();
  const videoId = offer.company_contact_url;

  const videoSource = videoId ? getFileStreamUrl(videoId) : null;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Handlers to play/pause video on hover
  const startVideo = () => {
    if (videoRef.current && videoRef.current.paused) {
      void videoRef.current.play().catch(() => {});
    }
  };

  const stopVideo = () => {
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current?.pause();
    }
  };

  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);

  const currency = offer.currency || '€';
  const salary =
    offer?.min_salary && offer?.max_salary && parseFloat(offer.max_salary) > 0
      ? offer.min_salary === offer.max_salary
        ? `${formatNumber(offer.min_salary)}${currency}`
        : `${formatNumber(offer.min_salary)} - ${formatNumber(offer.max_salary)}${currency}`
      : offer?.min_salary
        ? `${t('text811')} ${formatNumber(offer.min_salary)}${currency}`
        : t('text935');

  const isDualEducation = offer?.tags?.some(tag => tag.id === 4);

  const isTopped = !!offer?.topped_up_to && new Date(offer.topped_up_to).getTime() > Date.now();

  return (
    <div
      key={offer.slug}
      className="flex h-[370px] min-w-0 items-start justify-start"
      onMouseEnter={startVideo}
      onMouseLeave={stopVideo}
      onTouchStart={startVideo}
      onTouchEnd={stopVideo}
    >
      <div
        className={cn(
          'group relative flex h-full w-full min-w-0 flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-sm transition hover:shadow-md',
          isTopped && 'border-[3px] border-primary hover:border-primary',
        )}
      >
        <Link href={offer.slug} prefetchOnHover className="flex h-full min-h-0 w-full flex-col">
          <div className="relative flex min-h-0 w-full flex-1 flex-col">
            <div className="relative h-[160px] w-full shrink-0">
              {videoSource && (
                <div onClick={e => e.stopPropagation()} className="h-full w-full">
                  <video
                    ref={videoRef}
                    src={videoSource}
                    poster={offer.company?.image?.url || '/images/placeholder.png'}
                    preload="none"
                    playsInline
                    className="h-full w-full bg-black object-contain"
                    onPlay={handlePlay}
                    onPause={handlePause}
                  />
                </div>
              )}
              {!videoSource && offer?.company?.image?.url && (
                <Image
                  src={offer?.company?.image?.url || '/images/placeholder.png'}
                  alt={resolveImageAlt(offer?.company?.image, offer?.name)}
                  fill
                  className="object-contain"
                  sizes="(max-width: 768px) 100vw, 300px"
                />
              )}
              {!videoSource && !offer?.company?.image?.url && (
                <div className="flex h-full w-full items-center justify-center bg-primary/30">
                  <FiBriefcase className="h-full w-full" />
                </div>
              )}
              {!!isTopped && (
                <>
                  {/* Case 1: Only tag 2 exists */}
                  {offer.tags?.find(tag => tag.id === 2) &&
                    !offer.tags?.find(tag => tag.id === 3) && (
                      <div className="absolute right-0 top-0 z-[10] flex h-[30px] w-auto items-center justify-center rounded-bl-lg bg-primary px-4 text-sm font-bold text-white">
                        {offer.tags.find(tag => tag.id === 2)?.name}
                      </div>
                    )}

                  {/* Case 2: Only tag 3 exists */}
                  {!offer.tags?.find(tag => tag.id === 2) &&
                    offer.tags?.find(tag => tag.id === 3) && (
                      <div className="absolute right-0 top-0 z-[10] flex h-[30px] w-auto items-center justify-center rounded-bl-lg bg-primary px-4 text-sm font-bold text-white">
                        {offer.tags.find(tag => tag.id === 3)?.name}
                      </div>
                    )}

                  {/* Case 3: Both tags exist - tag 3 on top, tag 2 below */}
                  {offer.tags?.find(tag => tag.id === 2) && offer.tags?.find(tag => tag.id === 3) && (
                    <div className="absolute right-0 top-0 z-[10] flex flex-col">
                      <div className="flex h-[30px] w-auto items-center justify-center bg-primary px-4 text-sm font-bold text-white">
                        {offer.tags.find(tag => tag.id === 3)?.name}
                      </div>
                      <div className="flex h-[30px] w-auto items-center justify-center rounded-bl-lg bg-primary px-4 text-sm font-bold text-white">
                        {offer.tags.find(tag => tag.id === 2)?.name}
                      </div>
                    </div>
                  )}
                </>
              )}

              {isDualEducation && (
                <div className="absolute bottom-0 right-0 z-[10] flex h-[30px] w-[170px] items-center justify-center whitespace-nowrap rounded-tl-lg bg-primary text-sm font-bold text-white">
                  {t('Duálne vzdelávanie')}
                </div>
              )}
            </div>
            <div className="relative flex min-h-0 min-w-0 flex-1 flex-col gap-1 px-4 py-2">
              <div
                className="line-clamp-2 h-10 shrink-0 break-words text-sm font-bold leading-5 text-foreground"
                title={offer.name}
              >
                {offer.name}
              </div>
              <div className="line-clamp-1 h-4 shrink-0 text-xs leading-4 text-muted-foreground">
                {offer.company?.name}
              </div>
              <div className="flex h-4 min-w-0 shrink-0 items-center gap-1 text-xs leading-4 text-primary">
                {(offer.city || offer.country) && <RiMapPin2Line size={16} className="shrink-0" />}
                {offer.city && (
                  <span className="truncate">
                    {offer.city}, <span className="uppercase">{offer.country?.id || ''}</span>
                  </span>
                )}
                {!offer.city && offer.country?.name && (
                  <span className="truncate uppercase">{offer.country?.name}</span>
                )}
              </div>
              <div className="flex h-[22px] min-w-0 shrink-0 gap-1 md:h-[33px]">
                {offer.categories.length > 0 && (
                  <>
                    <div className="min-w-0">
                      <CategoryBadge
                        key={offer.categories[0].id}
                        category={offer.categories[0]}
                        maxWidth="100%"
                        asLink={false}
                      />
                    </div>
                    {offer.categories.length > 1 && (
                      <span className="flex shrink-0 items-center rounded-[8px] bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                        +{offer.categories.length - 1}
                      </span>
                    )}
                  </>
                )}
              </div>
              {isTopped && (
                <Star className="absolute bottom-[-100px] right-[-40px] h-40 w-40 text-yellow-400 opacity-20" />
              )}
            </div>
          </div>
          <div
            className={cn(
              'mt-auto flex w-full shrink-0 flex-col gap-2 p-4 pt-0',
              isTopped && 'pb-3.5',
            )}
          >
            {!isDualEducation && (
              <div className="mt-1 text-sm font-medium text-foreground">{salary}</div>
            )}
            <div className={cn('text-[10px] text-muted-foreground')}>
              {t('Pridané')}:{' '}
              {offer.published_at ? format(new Date(offer.published_at), 'dd.MM.yyyy') : '---'}
            </div>
          </div>
        </Link>
        {videoSource && !isPlaying && (
          <button
            type="button"
            aria-label={`Prehrať video: ${offer.name}`}
            className="absolute inset-x-0 top-0 z-20 flex h-[160px] items-center justify-center"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              if (videoRef.current?.paused) {
                void videoRef.current.play().catch(() => {});
              } else {
                videoRef.current?.pause();
              }
            }}
          >
            <PlayCircle className="h-16 w-16 text-white opacity-75 transition hover:opacity-100" />
          </button>
        )}
      </div>
    </div>
  );
};
